import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import { LockedBadge } from "./LockedFeature";
import { navForSpace, NAV_SPACES } from "../config/navigation";
import { Zap, Sparkles, LogOut, ArrowLeftRight, Menu, X } from "lucide-react";

const SPACE_LABEL = { CREATOR: "Mon espace", MANAGER: "Espace Manager", PROFESSIONAL: "Espace Pro" };
const SPACE_ICON = { CREATOR: "✦", MANAGER: "🧑‍💼", PROFESSIONAL: "💼" };

export function AppLayout({ children }) {
  const { user, logout, identity, activeSpace, switchSpace, featureState } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const currentSpace = NAV_SPACES.includes(activeSpace) ? activeSpace : "CREATOR";
  const isManagerSpace = currentSpace === "MANAGER";
  // Espaces disponibles ordonnés (rôles ∩ espaces gérés). Rotation cyclique -> tous atteignables.
  const availableSpaces = NAV_SPACES.filter((p) => (identity?.profiles || []).includes(p));
  const curIdx = availableSpaces.indexOf(currentSpace);
  const nextSpace = availableSpaces.length > 1
    ? availableSpaces[(curIdx + 1 + availableSpaces.length) % availableSpaces.length]
    : null;

  const nav = navForSpace(currentSpace, { featureState });
  const mobileItems = nav.filter((n) => n.mobile);
  const fab = nav.find((n) => n.fab);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const doSwitch = async (space) => {
    await switchSpace(space);
    navigate("/dashboard"); // DashboardRouter choisit le bon dashboard selon le nouvel espace
    setMenuOpen(false);
  };

  const homePath = isManagerSpace ? "/manager" : "/dashboard";

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-zinc-100 flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-zinc-800/80 bg-[#0C0C0F] p-5 sticky top-0 h-screen overflow-y-auto">
        <button onClick={() => navigate(homePath)} className="flex items-center gap-2 mb-6 px-2 text-left">
          <img src={process.env.PUBLIC_URL + "/cehnura-mark.png"} alt="Cehnura" className="w-9 h-9 object-contain" />
          <span className="font-display font-extrabold text-xl tracking-tight">Cehn<span className="gold-text">ura</span></span>
        </button>
        {nextSpace && (
          <button onClick={() => doSwitch(nextSpace)} data-testid="space-switch-btn"
            className="mb-4 flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold border border-zinc-800 bg-zinc-900 hover:border-amber-500/50 transition-colors">
            <ArrowLeftRight className="w-4 h-4 text-amber-400" />
            <span className="text-zinc-300">{SPACE_LABEL[currentSpace]}</span>
            <span className="ml-auto text-amber-400">→ {SPACE_LABEL[nextSpace]}</span>
          </button>
        )}
        <nav className="flex flex-col gap-1 flex-1">
          {nav.map((n) => (
            <NavLink key={n.id} to={n.path} end={n.end} data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive ? "bg-amber-500/15 text-amber-400 border border-amber-500/30"
                    : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 border border-transparent"
                }`}>
              <n.icon className="w-5 h-5 shrink-0" />
              {n.label}
              {n.state === "LOCKED" ? <LockedBadge upgrade={identity?.features?.[n.feature]?.upgrade_required} />
                : n.highlight ? <span className="ml-auto w-2 h-2 rounded-full bg-amber-400 animate-pulse" /> : null}
            </NavLink>
          ))}
        </nav>
        <button onClick={handleLogout} data-testid="logout-btn"
          className="flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-all">
          <LogOut className="w-5 h-5" /> Déconnexion
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 flex items-center justify-between px-4 md:px-8 h-16 border-b border-zinc-800/80 bg-[#0A0A0C]/80 backdrop-blur-xl">
          <div className="flex items-center gap-2 md:hidden">
            <img src={process.env.PUBLIC_URL + "/cehnura-mark.png"} alt="Cehnura" className="w-8 h-8 object-contain" />
            <span className="font-display font-extrabold text-lg">Cehn<span className="gold-text">ura</span></span>
          </div>
          <div className="hidden md:flex items-center">
            <span className="text-xs font-mono uppercase tracking-wider px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-400" data-testid="current-space-badge">
              {SPACE_ICON[currentSpace]} {SPACE_LABEL[currentSpace]}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {nextSpace && (
              <button onClick={() => doSwitch(nextSpace)} data-testid="space-switch-btn-header"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 hover:border-amber-500/50 text-xs text-zinc-300 transition-colors">
                <ArrowLeftRight className="w-3.5 h-3.5 text-amber-400" />
                {SPACE_LABEL[nextSpace]}
              </button>
            )}
            {currentSpace === "CREATOR" && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800" data-testid="header-xp">
                <Zap className="w-4 h-4 text-amber-400" fill="currentColor" />
                <span className="font-mono text-sm text-amber-400 font-semibold">{user?.total_xp ?? 0} XP</span>
                <span className="text-zinc-600">·</span>
                <span className="font-mono text-sm text-zinc-300">Niv. {user?.level ?? 1}</span>
              </div>
            )}
            <NotificationBell />
          </div>
        </header>

        <main className="flex-1 px-4 md:px-8 py-6 pb-28 md:pb-10 max-w-6xl w-full mx-auto">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 h-[68px] bg-[#0C0C0F]/95 backdrop-blur-xl border-t border-zinc-800 flex items-center justify-around px-2">
        {fab ? (
          <>
            {mobileItems.filter((n) => !n.fab).slice(0, 2).map((n) => <MobileItem key={n.id} n={n} />)}
            <NavLink to={fab.path} data-testid="nav-assistant-mobile" className="relative -mt-8 flex flex-col items-center">
              {({ isActive }) => (
                <>
                  <span className={`w-14 h-14 rounded-2xl gold-gradient flex items-center justify-center glow-gold assistant-pulse ${isActive ? "scale-105" : ""} transition-transform`}>
                    <Sparkles className="w-6 h-6 text-black" />
                  </span>
                  <span className="text-[10px] mt-1 font-medium text-amber-400">{fab.label}</span>
                </>
              )}
            </NavLink>
            {mobileItems.filter((n) => !n.fab).slice(2, 4).map((n) => <MobileItem key={n.id} n={n} />)}
            <button onClick={() => setMenuOpen(true)} data-testid="nav-more-mobile" className="flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg text-zinc-500">
              <Menu className="w-5 h-5" /><span className="text-[10px] font-medium">Plus</span>
            </button>
          </>
        ) : (
          <>
            {mobileItems.slice(0, 4).map((n) => <MobileItem key={n.id} n={n} />)}
            <button onClick={() => setMenuOpen(true)} data-testid="nav-more-mobile" className="flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg text-zinc-500">
              <Menu className="w-5 h-5" /><span className="text-[10px] font-medium">Plus</span>
            </button>
          </>
        )}
      </nav>

      {/* Mobile full menu sheet */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex flex-col justify-end" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <div className="relative bg-[#0C0C0F] border-t border-zinc-800 rounded-t-3xl p-5 pb-8 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <span className="font-display font-bold text-lg">Menu</span>
              <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" className="text-zinc-500 hover:text-zinc-200"><X className="w-5 h-5" /></button>
            </div>
            {nextSpace && (
              <button onClick={() => doSwitch(nextSpace)} data-testid="space-switch-mobile"
                className="w-full mb-4 flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-semibold gold-gradient text-black">
                <ArrowLeftRight className="w-4 h-4" /> Basculer vers {SPACE_LABEL[nextSpace]}
              </button>
            )}
            <div className="grid grid-cols-3 gap-3">
              {nav.map((n) => (
                <NavLink key={n.id} to={n.path} onClick={() => setMenuOpen(false)} data-testid={`nav-${n.id}-sheet`}
                  className={({ isActive }) => `relative flex flex-col items-center gap-2 p-3 rounded-2xl border ${isActive ? "border-amber-500/40 bg-amber-500/10 text-amber-400" : "border-zinc-800 text-zinc-300"}`}>
                  <n.icon className="w-5 h-5" />
                  <span className="text-[11px] text-center leading-tight">{n.label}</span>
                  {n.state === "LOCKED" && <span className="absolute top-1 right-1 text-[9px] text-amber-400">🔒</span>}
                </NavLink>
              ))}
              <button onClick={handleLogout} data-testid="logout-btn-mobile" className="flex flex-col items-center gap-2 p-3 rounded-2xl border border-zinc-800 text-red-400">
                <LogOut className="w-5 h-5" /><span className="text-[11px]">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MobileItem({ n }) {
  return (
    <NavLink to={n.path} end={n.end} data-testid={`nav-${n.id}-mobile`}
      className={({ isActive }) => `relative flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${isActive ? "text-amber-400" : "text-zinc-500"}`}>
      <n.icon className="w-5 h-5" />
      <span className="text-[10px] font-medium">{n.label}</span>
      {n.state === "LOCKED" && <span className="absolute top-0 right-1 text-[8px]">🔒</span>}
    </NavLink>
  );
}
